import { hrefOf } from '../overview/href'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  extendedMonitoringQuery,
  killProcess,
  logOffExtendedMonitoring,
  restartService,
  type DeviceExtendedMonitoringPage,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import { MonitoringChart } from './MonitoringChart'
import type { ScreenProps } from './screens'

/**
 * Fila Agentbased Extended Monitoring - extendedMonitoring.php + filele ei.
 * Aceleași 12 taburi, aceleași tabele, aceleași scrieri (Kill / Restart / Log Off).
 */

export function DeviceExtendedMonitoring({ id }: ScreenProps) {
  const { data, error } = useQuery(extendedMonitoringQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Board page={data} />
}

function Board({ page }: { page: DeviceExtendedMonitoringPage }) {
  const [active, setActive] = useState(page.tabs[0]?.slug ?? 'network_statistics')
  const tab = page.tabs.find((t) => t.slug === active)

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs monitoringList" role="tablist">
          {page.tabs.map((t) => (
            <li key={t.slug} className={t.slug === active ? 'active' : undefined}>
              <a
                href={hrefOf(t.href + page.device.id) ?? '#'}
                onClick={(ev) => {
                  ev.preventDefault()
                  setActive(t.slug)
                }}
              >
                <i className={t.icon} aria-hidden="true"></i> {t.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {!tab || !tab.allowed ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> <strong>Error.</strong> Please try again later.
            </div>
          ) : (
            <TabBody page={page} slug={tab.slug} />
          )}
        </div>
      </div>
    </div>
  )
}

function TabBody({ page, slug }: { page: DeviceExtendedMonitoringPage; slug: string }) {
  switch (slug) {
    case 'network_statistics':
      return <NetworkStatistics page={page} />
    case 'network_latency':
      return (
        <>
          <label className="color">
            <strong>Network Latency</strong> Last 24 Hours
          </label>
          <div className="network_latency_charts_container">
            {page.network_latency.charts && (
              <MonitoringChart title="{name}" charts={page.network_latency.charts} kind="spline" yTitle="Latency (miliseconds)" pointUnit="miliseconds" boxStyle="network" />
            )}
          </div>
        </>
      )
    case 'process_load':
      return <ProcessLoad page={page} />
    case 'stopped_processes':
      return <ServiceErrors page={page} />
    case 'disc_io':
      return (
        <>
          <label className="color">
            <strong>Disc I/O statistics</strong> Last 24 Hours
          </label>
          <div className="disc-io-charts">
            {page.disc_io.charts && (
              <MonitoringChart title="Volume {name}" charts={page.disc_io.charts} kind="area" yTitle="Bytes / Second" pointUnit="bytes" boxStyle="disc" />
            )}
          </div>
        </>
      )
    case 'current_sessions':
      return <CurrentSessions page={page} />
    case 'hdd_smart':
      return <Smart page={page} />
    case 'encryption':
      return <Encryption page={page} />
    case 'raid':
      return <Raid page={page} />
    case 'backup_jobs':
      return <Backups page={page} />
    case 'certificates':
      return <Certs page={page} />
    case 'server_hardware':
      return <Bmc page={page} />
    default:
      return null
  }
}

function NetworkStatistics({ page }: { page: DeviceExtendedMonitoringPage }) {
  const last = page.network_statistics.last ?? []
  return (
    <>
      <label className="color">
        <strong>Network Data</strong> Last Received
      </label>
      <div style={{ marginBottom: 20 }}>
        <table className="widget-table">
          <thead>
            <tr>
              <th className="a-left">Adapter name</th>
              <th className="a-left">Bytes Received</th>
              <th className="a-left">Bytes Sent</th>
              <th className="a-left">Bytes Total</th>
              <th className="a-left">Current bandwidth</th>
              <th className="a-left">Packets outbound errors</th>
              <th className="a-left">Packets received errors</th>
              <th className="a-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {last.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <center>Nothing to see yet...</center>
                </td>
              </tr>
            ) : (
              last.map((row) => (
                <tr key={row.name + row.date}>
                  <td>{row.name}</td>
                  <td>{row.bytes_received}</td>
                  <td>{row.bytes_sent}</td>
                  <td>{row.bytes_total}</td>
                  <td>{row.bandwidth}</td>
                  <td>{row.packets_out_errors}</td>
                  <td>{row.packets_in_errors}</td>
                  <td>{row.date}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <hr />
      <label className="color">
        <strong>Network Statistics</strong> Last 24 Hours
      </label>
      <div className="statistics_charts_container">
        {page.network_statistics.charts && (
          <MonitoringChart title="{name}" charts={page.network_statistics.charts} kind="area" yTitle="Bytes" pointUnit="bytes" boxStyle="network" />
        )}
      </div>
    </>
  )
}

function ProcessLoad({ page }: { page: DeviceExtendedMonitoringPage }) {
  const rows = page.process_load.rows ?? []
  const query = useQueryClient()
  const [killed, setKilled] = useState<Record<string, boolean>>({})
  return (
    <div className="row">
      <div className="col-md-12">
        {rows.length === 0 ? (
          <div className="alert alert-info">
            <strong>
              <i className="fas fa-info-circle"></i>
            </strong>{' '}
            Nothing to see yet...
          </div>
        ) : (
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th className="a-left">Process name</th>
                <th className="a-left">CPU utilization</th>
                <th className="a-left">Memory utilization</th>
                <th className="a-left">Date</th>
                <th className="a-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const done = row.killed || killed[row.resource_name]
                return (
                  <tr key={row.resource_name}>
                    <td>{row.resource_name}</td>
                    <td>{row.cpu}</td>
                    <td>{row.memory}</td>
                    <td>{row.created}</td>
                    <td>
                      {done ? (
                        <span className="btn btn-success btn-sm">Killed</span>
                      ) : (
                        <form
                          className="kill_process"
                          onSubmit={(ev) => {
                            ev.preventDefault()
                            if (!confirm('Are you sure ?')) return
                            void killProcess(page.device.id, row.resource_name).then(() => {
                              setKilled((s) => ({ ...s, [row.resource_name]: true }))
                              void query.invalidateQueries({ queryKey: ['device', page.device.id, 'extended-monitoring'] })
                            })
                          }}
                        >
                          <button className="btn btn-danger btn-sm" type="submit">
                            <i className="fas fa-times"></i> Kill
                          </button>
                        </form>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function ServiceErrors({ page }: { page: DeviceExtendedMonitoringPage }) {
  const rows = page.stopped_processes.rows ?? []
  const query = useQueryClient()
  const [restarted, setRestarted] = useState<Record<string, boolean>>({})
  return (
    <div className="row">
      <div className="col-md-12">
        {rows.length === 0 ? (
          <div className="alert alert-success">
            <strong>Great!</strong> No problems detected.
          </div>
        ) : (
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th className="a-left">Name</th>
                <th className="a-left">Exit Code</th>
                <th width={400} className="a-left">
                  Description
                </th>
                <th className="a-left">Date</th>
                <th className="a-left" width={150}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const done = row.restarted || restarted[row.name]
                return (
                  <tr key={row.name + row.created}>
                    <td>{row.display_name}</td>
                    <td>{row.exit_code}</td>
                    <td>{row.description}</td>
                    <td>{row.created}</td>
                    <td>
                      {done ? (
                        <span className="metro-btn x-small red">Restarted</span>
                      ) : (
                        <form
                          className="restart_service"
                          onSubmit={(ev) => {
                            ev.preventDefault()
                            if (!confirm('Are you sure ?')) return
                            void restartService(page.device.id, row.name).then(() => {
                              setRestarted((s) => ({ ...s, [row.name]: true }))
                              void query.invalidateQueries({ queryKey: ['device', page.device.id, 'extended-monitoring'] })
                            })
                          }}
                        >
                          <button className="btn btn-success btn-sm" type="submit">
                            <i className="fas fa-undo"></i> Restart
                          </button>
                        </form>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function CurrentSessions({ page }: { page: DeviceExtendedMonitoringPage }) {
  const sessions = page.current_sessions.sessions ?? []
  const logins = page.current_sessions.logins ?? []
  const query = useQueryClient()
  const [off, setOff] = useState<Record<string, boolean>>({})
  return (
    <>
      <div className="row">
        {sessions.length === 0 ? (
          <div className="alert alert-info">
            <strong>
              <i className="fas fa-info-circle"></i>
            </strong>{' '}
            No active sessions.
          </div>
        ) : (
          sessions.map((session) => {
            const logged = session.logged_off || off[session.id]
            return (
              <div className="row" key={session.id}>
                <div className=" col-md-3">
                  <div className="bg-metal color-white current_sessions">
                    <div className={'user-icon ' + (logged ? 'color-red' : 'color-green2') + ' color-white round'}>
                      <i className="icon-user"></i>
                    </div>
                    <h4 style={{ fontWeight: 'normal', marginBottom: 10, color: '#ddd' }}>
                      {session.domain ? session.domain + '\\' : ''}
                      {session.user_name}
                    </h4>
                    <ul className="color-white" style={{ fontSize: 10 }}>
                      <li>
                        <label className="color-quartz">SID:</label> {session.sid}
                      </li>
                      <li>
                        <label className="color-quartz">LOGIN TYPE:</label> {session.login_type}
                      </li>
                      <li>
                        <label className="color-quartz">LOGON ID:</label> {session.logon_id}
                      </li>
                      <li>
                        <label className="color-quartz">LOCAL PATH:</label> {session.local_path}
                      </li>
                      <li>
                        <label className="color-quartz">LOCAL PATH SIZE:</label> {session.local_path_size}
                      </li>
                      <li>
                        <label className="color-quartz">ROAMING PROFILE:</label> {session.roaming_profile}
                      </li>
                      <li>
                        <label className="color-quartz">ROAMING PROFILE PATH:</label> {session.roaming_profile_path ? session.roaming_profile_path : 'n/a'}
                      </li>
                      <li>
                        <label className="color-quartz">ROAMING PROFILE PATH SIZE:</label> {session.roaming_profile_path_size}
                      </li>
                    </ul>
                    <form
                      className="log_off_user"
                      onSubmit={(ev) => {
                        ev.preventDefault()
                        if (!confirm('Are you sure ?')) return
                        void logOffExtendedMonitoring(page.device.id, session.user_name, session.id).then(() => {
                          setOff((s) => ({ ...s, [session.id]: true }))
                          void query.invalidateQueries({ queryKey: ['device', page.device.id, 'extended-monitoring'] })
                        })
                      }}
                    >
                      <button type="submit" className="btn-outline btn white" disabled={logged} style={logged ? { backgroundColor: '#ccc' } : undefined}>
                        <i className="fas fa-power-off"></i> Log Off
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
      <div className="row" style={{ marginTop: 18 }}>
        <div className="col-md-12">
          <h4 className="bordered">Recent logins</h4>
          {logins.length === 0 ? (
            <p className="color-light-grey" style={{ fontSize: 12 }}>
              <i className="fas fa-info-circle"></i> Nothing recorded yet. The log is built from what changes between two reports, so it starts filling in from the
              machine&apos;s next one.
            </p>
          ) : (
            <table className="table table-hover" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ width: 170 }}>Logged in</th>
                  <th style={{ width: 170 }}>Logged out</th>
                  <th style={{ width: 120 }}>For</th>
                </tr>
              </thead>
              <tbody>
                {logins.map((login) => (
                  <tr key={login.user + login.at}>
                    <td>
                      {login.user}
                      {login.open && (
                        <i className="fas fa-circle color-green2 font-10" data-toggle="tooltip" data-placement="top" title="Still logged in at the last report"></i>
                      )}
                      {login.type !== '' && <div className="color-light-grey" style={{ fontSize: 11 }}>{login.type}</div>}
                    </td>
                    <td className="color-light-grey">{login.at}</td>
                    <td className="color-light-grey">{login.open ? <span className="color-green2">still on</span> : login.left_at}</td>
                    <td className="color-light-grey">{login.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

function Smart({ page }: { page: DeviceExtendedMonitoringPage }) {
  const disks = page.hdd_smart.disks ?? []
  if (disks.length === 0) {
    return (
      <div className="alert alert-info">
        <strong>
          <i className="fas fa-info-circle"></i>
        </strong>{' '}
        Nothing to see yet...
      </div>
    )
  }
  return (
    <>
      {disks.map((disk) => (
        <div key={disk.index}>
          <h4>
            <i className="far fa-hdd" aria-hidden="true"></i> {disk.index}
          </h4>
          <hr />
          <div className="row">
            <div className="col-md-12">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th className="a-left">Disk property</th>
                    <th className="a-left">Current</th>
                    <th className="a-left">Worst</th>
                    <th className="a-left">Treshold</th>
                    <th className="a-left">Data</th>
                    <th className="a-left">Is OK</th>
                  </tr>
                </thead>
                <tbody>
                  {disk.attributes.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <center>Nothing to see yet...</center>
                      </td>
                    </tr>
                  ) : (
                    disk.attributes.map((a) => (
                      <tr key={a.name}>
                        <td>{a.name}</td>
                        <td>{a.current}</td>
                        <td>{a.worst}</td>
                        <td>{a.treshold}</td>
                        <td>{a.data}</td>
                        <td>
                          {a.is_ok ? <i className="color-green fas fa-check-circle"></i> : <i className="color-red fas fa-minus-circle"></i>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function Encryption({ page }: { page: DeviceExtendedMonitoringPage }) {
  const volumes = page.encryption.volumes ?? []
  return (
    <>
      <style>{`
        .enc-lead { color: #6b7682; margin: 0 0 12px; }
        .enc-lead b.is-bad { color: #C43626; }
        .enc-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; margin-left: 6px; vertical-align: middle; }
        .enc-chip.is-ok { background: #e6f5ec; color: #1f7a3f; }
        .enc-chip.is-bad { background: #fbe3e0; color: #C43626; }
        .enc-chip.is-warn { background: #fff3dc; color: #9e6b00; }
        .partition_tile.is-bad { border-color: #e8a49c; }
      `}</style>
      {volumes.length === 0 ? (
        <div className="alert alert-info">
          <strong>
            <i className="fas fa-info-circle"></i>
          </strong>{' '}
          {page.encryption.lead}
        </div>
      ) : (
        <>
          <p className="enc-lead">
            {(page.encryption.unprotected ?? 0) === 0 ? (
              'Every data volume on this machine is encrypted and protected.'
            ) : (
              <>
                <b className="is-bad">{page.encryption.unprotected}</b> of {volumes.length} data volume
                {volumes.length === 1 ? '' : 's'} {page.encryption.unprotected === 1 ? 'is' : 'are'} not protected. If this
                machine leaves the building, whatever is on {page.encryption.unprotected === 1 ? 'it' : 'them'} leaves in the
                clear.
              </>
            )}
          </p>
          <div className="row">
            {volumes.map((v) => (
              <div className="col-md-4" key={v.letter + v.title_name}>
                <div className={'partition_tile border-steel bg-white' + (v.protected ? '' : ' is-bad')} style={{ width: '100%' }}>
                  <div className="partition_tile_title col-md-7">
                    <i className={'fa ' + (v.protected ? 'fa-lock' : 'fa-unlock')}></i>{' '}
                    {v.title_name !== '' ? (
                      <>
                        {v.title_name} <small>{v.letter}</small>
                      </>
                    ) : (
                      v.letter
                    )}
                    <span className={'enc-chip ' + v.chip.tone}>{v.chip.text}</span>
                  </div>
                  <ul className="tile_list col-md-7">
                    <li>{v.size} volume</li>
                    <li>
                      <br />
                    </li>
                    <li>
                      <strong>Status: </strong>
                      {v.status}
                    </li>
                    <li>
                      <strong>Encryption: </strong>
                      {v.encryption}
                    </li>
                    <li>
                      <strong>Protection: </strong>
                      {v.protection}
                    </li>
                    {v.lock !== '' && (
                      <li>
                        <strong>Lock status: </strong>
                        {v.lock}
                      </li>
                    )}
                    {v.auto_unlock !== '' && v.auto_unlock !== 'No' && (
                      <li>
                        <strong>Auto unlock: </strong>
                        {v.auto_unlock}
                      </li>
                    )}
                    {v.id_field !== '' && (
                      <li className="text-muted">
                        <small>{v.id_field}</small>
                      </li>
                    )}
                  </ul>
                  <span data-line-cap="round" className="easychart partitions encrypted col-md-5" data-percent={v.percent}>
                    <svg width={100} height={100} viewBox="0 0 100 100" style={{ display: 'block' }}>
                      <circle cx={50} cy={50} r={42} fill="none" stroke="#FFCC33" strokeWidth={8} />
                      <circle
                        cx={50}
                        cy={50}
                        r={42}
                        fill="none"
                        stroke="#329531"
                        strokeWidth={8}
                        strokeDasharray={`${(v.percent / 100) * 2 * Math.PI * 42} ${2 * Math.PI * 42}`}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <span className="percent_partitions">
                      <i className="fa fa-lock"></i>
                      <br />
                      {v.percent}%
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function Raid({ page }: { page: DeviceExtendedMonitoringPage }) {
  const [open, setOpen] = useState({ arrays: true, volumes: true, hp: true })
  const toggle = (key: 'arrays' | 'volumes' | 'hp') => setOpen((s) => ({ ...s, [key]: !s[key] }))
  if (page.raid.empty) {
    return (
      <div className="alert alert-info">
        <strong>
          <i className="fas fa-info-circle"></i>
        </strong>{' '}
        No RAID array reported on this machine. Software RAID (mdadm, ZFS, Storage Spaces, AppleRAID) is read directly; hardware controllers need the vendor tool
        on the box - storcli / perccli, ssacli, MegaCli or arcconf.
      </div>
    )
  }
  return (
    <>
      {(page.raid.arrays ?? []).length > 0 && (
        <div className={'widget ' + (page.raid.widget ?? 'bg-green')} id="raid_arrays">
          <div className="widget-header">
            <div className="widget-icon">
              <i className="fas fa-hdd"></i>
            </div>
            <h2 className="widget-title">RAID arrays</h2>
            <div className="widget-action">
              <button className="btn expand-widget color-white" data-toggle="collapse" type="button" onClick={() => toggle('arrays')}>
                <i className={open.arrays ? 'fas fa-minus' : 'fas fa-plus'}></i>
              </button>
            </div>
          </div>
          {open.arrays && (
            <div className="widget-content bg-white" data-toggle="to-collapse">
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Array</th>
                    <th>Level</th>
                    <th>State</th>
                    <th>Disks</th>
                    <th>Controller / source</th>
                    <th>Detail</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {(page.raid.arrays ?? []).map((a) => (
                    <tr key={a.array_id}>
                      <td>
                        {a.label_name !== '' && a.label_name !== a.array_id ? (
                          <>
                            {a.label_name} <small className="text-muted">{a.array_id}</small>
                          </>
                        ) : (
                          a.array_id
                        )}
                      </td>
                      <td>{a.level}</td>
                      <td>
                        <span className={'badge ' + a.badge.tone}>
                          <i className={a.badge.icon}></i> {a.badge.text}
                        </span>
                      </td>
                      <td>
                        {a.failed > 0 ? (
                          <>
                            {a.disks.replace(', ' + a.failed + ' failed', '')}
                            {a.disks.includes('failed') && (
                              <>
                                , <span className="color-red">{a.failed} failed</span>
                              </>
                            )}
                          </>
                        ) : (
                          a.disks
                        )}
                      </td>
                      <td>
                        {a.source}
                        {a.controller !== '' && (
                          <>
                            <br />
                            <small className="text-muted">{a.controller}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <small>{a.detail}</small>
                      </td>
                      <td>
                        <small>{a.updated}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {(page.raid.volumes ?? []).length > 0 && (
        <div className="widget bg-red" id="last_hour_network_statistics">
          <div className="widget-header">
            <div className="widget-icon">
              <i className="fa fa-users"></i>
            </div>
            <h2 className="widget-title">Raid volumes</h2>
            <div className="widget-action">
              <button className="btn expand-widget color-white" data-toggle="collapse" type="button" onClick={() => toggle('volumes')}>
                <i className={open.volumes ? 'fas fa-minus' : 'fas fa-plus'}></i>
              </button>
            </div>
          </div>
          {open.volumes && (
            <div className="widget-content bg-white" data-toggle="to-collapse">
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Volume</th>
                    <th>Letter</th>
                    <th>Label</th>
                    <th>File System</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Info</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(page.raid.volumes ?? []).map((v) => (
                    <tr key={v.volume + v.letter}>
                      <td>{v.volume}</td>
                      <td>{v.letter}</td>
                      <td>{v.label}</td>
                      <td>{v.file_system}</td>
                      <td>{v.type}</td>
                      <td>{v.size}</td>
                      <td>
                        {v.healthy ? <i className="fas fa-check-circle color-green"></i> : <i className="fas fa-exclamation-circle color-red"></i>} {v.status}
                      </td>
                      <td>{v.info}</td>
                      <td>{v.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {(page.raid.hp ?? []).length > 0 && (
        <div className="widget bg-red" id="last_hour_network_statistics">
          <div className="widget-header">
            <div className="widget-icon">
              <i className="fa fa-users"></i>
            </div>
            <h2 className="widget-title">HP</h2>
            <div className="widget-action">
              <button className="btn expand-widget color-white" data-toggle="collapse" type="button" onClick={() => toggle('hp')}>
                <i className={open.hp ? 'fas fa-minus' : 'fas fa-plus'}></i>
              </button>
            </div>
          </div>
          {open.hp && (
            <div className="widget-content bg-white" data-toggle="to-collapse">
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Slot</th>
                    <th>Port</th>
                    <th>Box</th>
                    <th>Bay</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(page.raid.hp ?? []).map((v) => (
                    <tr key={v.slot + v.bay}>
                      <td>{v.slot}</td>
                      <td>{v.port}</td>
                      <td>{v.box}</td>
                      <td>{v.bay}</td>
                      <td>{v.size}</td>
                      <td>
                        {v.ok ? <i className="fas fa-check-circle color-green"></i> : <i className="fas fa-exclamation-circle color-red"></i>} {v.status}
                      </td>
                      <td>{v.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function Backups({ page }: { page: DeviceExtendedMonitoringPage }) {
  const jobs = page.backup_jobs.jobs ?? []
  const failed = page.backup_jobs.failed ?? 0
  const stale = page.backup_jobs.stale ?? 0
  const count = page.backup_jobs.count ?? 0
  return (
    <>
      <style>{`
        .bk-lead { color: #6b7682; margin: 0 0 12px; }
        .bk-lead b.is-bad { color: #C43626; }
        .bk-lead b.is-warn { color: #9e6b00; }
        .bk-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; margin-left: 6px; vertical-align: middle; }
        .bk-chip.is-ok { background: #e6f5ec; color: #1f7a3f; }
        .bk-chip.is-bad { background: #fbe3e0; color: #C43626; }
        .bk-chip.is-warn { background: #fff3dc; color: #9e6b00; }
        .bk-chip.is-muted { background: #eef0f3; color: #6b7682; }
        .partition_tile.is-bad { border-color: #e8a49c; }
        .partition_tile.is-warn { border-color: #f0d28a; }
        .bk-tool { color: #6b7682; font-size: 12px; }
      `}</style>
      {jobs.length === 0 ? (
        <p className="bk-lead">
          No backup job found on this machine. The agent looks for Windows Server Backup, Veeam Agent and Veeam Backup &amp; Replication, SQL Server backups (per
          database), Time Machine on macOS, and systemd units that run restic, borg, bacula, pgbackrest, duplicity or rsnapshot on Linux. If this machine is backed
          up by something else - a hypervisor snapshot, a SaaS agent - it is not visible from here, and the machine itself cannot tell you whether it worked last
          night.
        </p>
      ) : (
        <>
          <p className="bk-lead">
            {page.backup_jobs.lead === 'ok' ? (
              'Every backup job on this machine succeeded the last time it ran, and none is older than three days.'
            ) : (
              <>
                {failed > 0 && (
                  <>
                    <b className="is-bad">{failed}</b> of {count} backup job{count === 1 ? '' : 's'} failed the last time {failed === 1 ? 'it' : 'they'} ran.
                  </>
                )}
                {stale > 0 && (
                  <>
                    {' '}
                    <b className="is-warn">{stale}</b> {stale === 1 ? 'has' : 'have'} no successful run in the last three days.
                  </>
                )}{' '}
                The day somebody needs a file back is the wrong day to learn this.
              </>
            )}
          </p>
          <div className="row">
            {jobs.map((job) => (
              <div className="col-md-4" key={job.name + job.tool}>
                <div className={'partition_tile border-steel bg-white' + job.tone} style={{ width: '100%' }}>
                  <div className="partition_tile_title col-md-12">
                    <i className={'fa ' + job.icon}></i> {job.name}
                    <span className={'bk-chip ' + job.chip.tone}>{job.chip.text}</span>
                    <div className="bk-tool">
                      {job.tool}
                      {job.target !== '' ? ' · ' + job.target : ''}
                    </div>
                  </div>
                  <ul className="tile_list col-md-12">
                    <li>
                      <strong>Last run: </strong>
                      {job.last_run}
                      {job.last_run_ago !== '' && job.last_run !== '-' ? <span className="text-muted"> ({job.last_run_ago})</span> : null}
                    </li>
                    <li>
                      <strong>Last success: </strong>
                      {!job.last_ok_empty ? (
                        <>
                          {job.last_ok} <span className="text-muted">({job.last_ok_ago})</span>
                        </>
                      ) : job.unknown ? (
                        <span className="text-muted">not known - only the folder&apos;s newest write</span>
                      ) : (
                        <span className="text-danger">none on record</span>
                      )}
                    </li>
                    {job.schedule !== '' && (
                      <li>
                        <strong>Next run: </strong>
                        {job.schedule}
                      </li>
                    )}
                    {job.detail !== '' && (
                      <li>
                        <strong>Tool says: </strong>
                        {job.detail}
                      </li>
                    )}
                    <li className="text-muted">
                      <small>Reported {job.updated}</small>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function Certs({ page }: { page: DeviceExtendedMonitoringPage }) {
  const certs = page.certificates.certs ?? []
  const expired = page.certificates.expired ?? 0
  const soon = page.certificates.soon ?? 0
  const count = page.certificates.count ?? 0
  return (
    <>
      <style>{`
        .ct-lead { color: #6b7682; margin: 0 0 12px; }
        .ct-lead b.is-bad { color: #C43626; }
        .ct-lead b.is-warn { color: #9e6b00; }
        .ct-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; margin-left: 6px; vertical-align: middle; white-space: nowrap; }
        .ct-chip.is-ok { background: #e6f5ec; color: #1f7a3f; }
        .ct-chip.is-bad { background: #fbe3e0; color: #C43626; }
        .ct-chip.is-warn { background: #fff3dc; color: #9e6b00; }
        .ct-chip.is-muted { background: #eef0f3; color: #6b7682; }
        .partition_tile.is-bad { border-color: #e8a49c; }
        .partition_tile.is-warn { border-color: #f0d28a; }
        .ct-issuer { color: #6b7682; font-size: 12px; }
        .ct-src { display: inline-block; background: #f3f5f7; border-radius: 4px; padding: 1px 6px; margin: 2px 4px 2px 0; font-size: 12px; font-family: Menlo, Consolas, monospace; }
        .ct-fp { color: #9aa3ad; font-family: Menlo, Consolas, monospace; font-size: 11px; word-break: break-all; }
      `}</style>
      {certs.length === 0 ? (
        <p className="ct-lead">
          No certificate found on this machine. The agent does a TLS handshake on every listening port that speaks TLS and reads the machine&apos;s stores:
          LocalMachine\My, WebHosting and Remote Desktop on Windows; /etc/letsencrypt, nginx, Apache, Postfix, Dovecot and Kubernetes paths on Linux;
          System.keychain on macOS. Public trust roots are left out on purpose - they are not yours to renew.
        </p>
      ) : (
        <>
          <p className="ct-lead">
            {page.certificates.lead === 'ok' ? (
              <>
                {count} certificate{count === 1 ? '' : 's'} on this machine, none expiring in the next 30 days.
              </>
            ) : (
              <>
                {expired > 0 && (
                  <>
                    <b className="is-bad">{expired}</b> of {count} certificate{count === 1 ? '' : 's'} {expired === 1 ? 'has' : 'have'} already expired.
                  </>
                )}
                {soon > 0 && (
                  <>
                    {' '}
                    <b className="is-warn">{soon}</b> expire{soon === 1 ? 's' : ''} within 30 days.
                  </>
                )}{' '}
                A certificate that expires on a domain controller or a mail server stops logins and mail, not just a browser padlock.
              </>
            )}
          </p>
          <div className="row">
            {certs.map((cert) => (
              <div className="col-md-4" key={cert.fingerprint}>
                <div className={'partition_tile border-steel bg-white' + cert.tone} style={{ width: '100%' }}>
                  <div className="partition_tile_title col-md-12">
                    <i className={'fa ' + cert.icon}></i> {cert.subject}
                    <span className={'ct-chip ' + cert.chip.tone}>{cert.chip.text}</span>
                    <div className="ct-issuer">
                      {cert.self_signed ? 'self-signed' : cert.issuer !== '' ? 'issued by ' + cert.issuer : ''}
                      {cert.is_ca ? ' · CA' : ''}
                      {cert.key_type !== '' ? ' · ' + cert.key_type : ''}
                    </div>
                  </div>
                  <ul className="tile_list col-md-12">
                    <li>
                      <strong>Valid: </strong>
                      {cert.not_before} → {cert.not_after}
                    </li>
                    {cert.sans.length > 0 && (
                      <li>
                        <strong>Names: </strong>
                        {cert.sans.join(', ')}
                        {cert.sans_more > 0 && <span className="text-muted"> +{cert.sans_more} more</span>}
                      </li>
                    )}
                    <li>
                      <strong>Found: </strong>
                      {cert.sources.map((src) => (
                        <span className="ct-src" key={src}>
                          {src}
                        </span>
                      ))}
                    </li>
                    <li className="ct-fp">SHA-256 {cert.fingerprint}</li>
                    <li className="text-muted">
                      <small>Reported {cert.updated}</small>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function Bmc({ page }: { page: DeviceExtendedMonitoringPage }) {
  const hw = page.server_hardware
  if (!hw.has) {
    return (
      <p className="bh-lead">
        No management controller readable on this machine. On a server the agent reads the BMC in-band - ipmitool or ipmiutil over /dev/ipmi0, Dell OpenManage
        (omreport) or HPE hpasmcli - without a BMC password or a management network. Install one of them (ipmitool on Linux, ipmiutil on Windows) and load the
        ipmi_si / ipmi_devintf modules on Linux, and the power supplies, fans, temperatures and inlet air appear here within ten minutes. Virtual machines and
        workstations have no BMC; ECC counters appear on Linux when the kernel has EDAC, on Windows from WHEA memory events.
      </p>
    )
  }
  const failedPsu = hw.failed_psu ?? 0
  const bad = hw.bad_sensors ?? 0
  const ecc = hw.ecc
  const healthy =
    failedPsu === 0 && bad === 0 && (!ecc || ((ecc.uncorrectable ?? 0) === 0 && (ecc.corrected ?? 0) === 0))
  return (
    <>
      <style>{`
        .bh-lead { color: #6b7682; margin: 0 0 12px; }
        .bh-lead b.is-bad { color: #C43626; }
        .bh-lead b.is-warn { color: #9e6b00; }
        .bh-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; margin-left: 6px; vertical-align: middle; white-space: nowrap; }
        .bh-chip.is-ok { background: #e6f5ec; color: #1f7a3f; }
        .bh-chip.is-bad { background: #fbe3e0; color: #C43626; }
        .bh-chip.is-warn { background: #fff3dc; color: #9e6b00; }
        .bh-chip.is-muted { background: #eef0f3; color: #6b7682; }
        .partition_tile.is-bad { border-color: #e8a49c; }
        .partition_tile.is-warn { border-color: #f0d28a; }
        .bh-table { width: 100%; font-size: 13px; }
        .bh-table td { padding: 4px 6px; border-bottom: 1px solid #eef0f3; vertical-align: top; }
        .bh-table td.num { text-align: right; white-space: nowrap; font-family: Menlo, Consolas, monospace; }
        .bh-table tr.is-bad td { background: #fff5f4; }
        .bh-table tr.is-warn td { background: #fffaf0; }
        .bh-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; background: #1f7a3f; }
        .bh-dot.is-warn { background: #d9a400; }
        .bh-dot.is-bad { background: #C43626; }
        .bh-dot.is-muted { background: #b8c0c8; }
      `}</style>
      <p className="bh-lead">
        {healthy ? (
          <>
            The management controller ({hw.source}) reports {hw.sensor_count} sensor{(hw.sensor_count ?? 0) === 1 ? '' : 's'} within thresholds
            {(hw.psu_count ?? 0) > 0 ? ', ' + hw.psu_count + ' power suppl' + ((hw.psu_count ?? 0) === 1 ? 'y' : 'ies') + ' in order' : ''}
            {ecc ? ', no memory error' : ''}.
          </>
        ) : (
          <>
            {failedPsu > 0 && (
              <>
                <b className="is-bad">{failedPsu}</b> of {hw.psu_count} power suppl{(hw.psu_count ?? 0) === 1 ? 'y is' : 'ies are'} not in order - the server has
                no redundancy left.
              </>
            )}
            {bad > 0 && (
              <>
                {' '}
                <b className={(hw.sensors_critical ?? 0) > 0 ? 'is-bad' : 'is-warn'}>{bad}</b> sensor{bad === 1 ? ' is' : 's are'} outside the vendor&apos;s
                thresholds.
              </>
            )}
            {ecc && ecc.uncorrectable > 0 && (
              <>
                {' '}
                <b className="is-bad">{ecc.uncorrectable}</b> uncorrectable memory error{ecc.uncorrectable === 1 ? '' : 's'} - a DIMM must be replaced.
              </>
            )}
            {ecc && ecc.uncorrectable === 0 && ecc.corrected > 0 && (
              <>
                {' '}
                <b className="is-warn">{ecc.corrected}</b> corrected memory error{ecc.corrected === 1 ? '' : 's'} {ecc.window === '24h' ? 'in 24 h' : 'since boot'}.
              </>
            )}
          </>
        )}
        <span className="text-muted">
          {' '}
          Read in-band through {hw.source}, {hw.updated}.
        </span>
      </p>
      <div className="row">
        {((hw.psus ?? []).length > 0 || hw.inlet) && (
          <div className="col-md-4">
            <div className={'partition_tile border-steel bg-white' + (failedPsu > 0 ? ' is-bad' : '')} style={{ width: '100%' }}>
              <div className="partition_tile_title col-md-12">
                <i className="fa fa-plug"></i> Power &amp; cooling
              </div>
              <ul className="tile_list col-md-12">
                {hw.inlet && (
                  <li>
                    <strong>Inlet air: </strong>
                    {hw.inlet} <span className={'bh-chip ' + (hw.inlet_tone ?? '')}>{hw.inlet_label}</span>
                  </li>
                )}
                {(hw.psus ?? []).map((p) => (
                  <li key={p.name}>
                    <span className={'bh-dot' + (p.ok ? '' : ' is-bad')}></span>
                    <strong>{p.name}: </strong>
                    {p.ok ? 'ok' : p.status}
                    {p.watts ? <span className="text-muted"> · {p.watts} W</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {ecc && (
          <div className="col-md-4">
            <div className={'partition_tile border-steel bg-white' + (ecc.uncorrectable > 0 ? ' is-bad' : ecc.corrected > 0 ? ' is-warn' : '')} style={{ width: '100%' }}>
              <div className="partition_tile_title col-md-12">
                <i className="fa fa-memory"></i> ECC memory{' '}
                <span className="bh-chip is-muted">
                  {ecc.window === '24h' ? 'last 24 h' : 'since boot'} · {ecc.source.toUpperCase()}
                </span>
              </div>
              <ul className="tile_list col-md-12">
                <li>
                  <strong>Corrected: </strong>
                  {ecc.corrected}
                </li>
                <li>
                  <strong>Uncorrectable: </strong>
                  {ecc.uncorrectable}
                </li>
                {ecc.dimms.map((d) => (
                  <li key={d.label}>
                    <span className={'bh-dot' + (d.uncorrectable > 0 ? ' is-bad' : d.corrected > 0 ? ' is-warn' : '')}></span>
                    {d.label !== '' ? d.label : 'memory'}: {d.corrected} corrected
                    {d.uncorrectable > 0 ? (
                      <>
                        , <b>{d.uncorrectable} uncorrectable</b>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {(hw.groups ?? []).map((g) => (
          <div className="col-md-4" key={g.type}>
            <div className={'partition_tile border-steel bg-white' + (g.bad ? ' is-bad' : g.warn ? ' is-warn' : '')} style={{ width: '100%' }}>
              <div className="partition_tile_title col-md-12">
                <i className={'fa ' + g.icon}></i> {g.label} <span className="bh-chip is-muted">{g.rows.length}</span>
              </div>
              <table className="bh-table">
                <tbody>
                  {g.rows.map((s) => {
                    const tone = s.status === 'critical' ? 'is-bad' : s.status === 'warning' ? 'is-warn' : s.status === 'unknown' ? 'is-muted' : ''
                    return (
                      <tr className={tone} key={s.name}>
                        <td>
                          <span className={'bh-dot ' + tone}></span>
                          {s.name}
                        </td>
                        <td className="num">{s.reading}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
